/**
 * PartnerMemberRestaurants — iter98
 * Searchable + filterable list of restaurants tied to the partner.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Search, Store } from 'lucide-react';
import PartnerAccountLayout from '../../../components/PartnerAccountLayout';
import { API, authHeaders, fmtDate } from './_utils';

const STATUS_BADGES = {
  trial:    { label: 'Trial',    cls: 'bg-amber-500/15   text-amber-300   border-amber-500/30' },
  active:   { label: 'Active',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  paid:     { label: 'Active',   cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  inactive: { label: 'Inactive', cls: 'bg-white/[0.04]   text-white/55    border-white/[0.08]' },
  pending:  { label: 'Pending',  cls: 'bg-blue-500/15    text-blue-300    border-blue-500/30' },
};

function StatusPill({ status }) {
  const b = STATUS_BADGES[status] || STATUS_BADGES.inactive;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10.5px] font-bold uppercase tracking-wider ${b.cls}`}>
      {b.label}
    </span>
  );
}

export default function PartnerMemberRestaurants() {
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/partners/referrals`, { headers: authHeaders() });
        setReferrals(r.data.items || []);
      } catch {
        toast.error('Could not load restaurants');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return referrals.filter((r) => {
      if (filter !== 'all') {
        if (filter === 'active' && !['active', 'paid'].includes(r.status)) return false;
        if (filter === 'trial' && r.status !== 'trial') return false;
        if (filter === 'inactive' && ['active', 'paid', 'trial'].includes(r.status)) return false;
      }
      if (q) {
        const hay = `${r.restaurant_name || ''} ${r.city || ''} ${r.state || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [referrals, query, filter]);

  return (
    <PartnerAccountLayout
      currentKey="restaurants"
      title="Member Restaurants"
      subtitle="Every restaurant signed up through your partner code, in one place."
    >
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, city, or state…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/45 text-[14px] focus:outline-none focus:border-gold-500/50"
            data-testid="partner-restaurants-search"
          />
        </div>
        <div className="flex gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/[0.08]">
          {[
            { k: 'all', l: 'All' },
            { k: 'active', l: 'Active' },
            { k: 'trial', l: 'Trial' },
            { k: 'inactive', l: 'Inactive' },
          ].map((f) => (
            <button
              key={f.k}
              onClick={() => setFilter(f.k)}
              data-testid={`partner-restaurants-filter-${f.k}`}
              className={`px-3 py-1.5 rounded-lg text-[12px] font-bold transition-colors ${
                filter === f.k ? 'bg-gold-500 text-black' : 'text-white/70 hover:text-white'
              }`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-gold-300" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/[0.08] bg-white/[0.015] p-10 text-center">
          <Store className="w-7 h-7 text-white/40 mx-auto mb-3" />
          <p className="text-sm font-bold text-white mb-1">No restaurants found</p>
          <p className="text-xs text-white/50 max-w-[420px] mx-auto">
            {referrals.length === 0 ? 'Share your referral link to start attracting member restaurants.' : 'Try a different search or filter.'}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-x-auto" data-testid="partner-restaurants-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {['Restaurant Name', 'Status', 'Trial', 'Active', 'Join Date', 'City', 'State'].map((h) => (
                  <th key={h} className="text-left text-[10.5px] uppercase tracking-[0.18em] text-white/45 font-bold px-5 py-3.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id || r.restaurant_id} className="border-b border-white/[0.03] last:border-0 hover:bg-white/[0.015]">
                  <td className="px-5 py-3.5 text-white/90 font-semibold whitespace-nowrap">{r.restaurant_name || '—'}</td>
                  <td className="px-5 py-3.5 whitespace-nowrap"><StatusPill status={r.status} /></td>
                  <td className="px-5 py-3.5 whitespace-nowrap text-white/70">{r.status === 'trial' ? 'Yes' : '—'}</td>
                  <td className="px-5 py-3.5 whitespace-nowrap text-white/70">{['active', 'paid'].includes(r.status) ? 'Yes' : '—'}</td>
                  <td className="px-5 py-3.5 text-white/75 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                  <td className="px-5 py-3.5 text-white/75 whitespace-nowrap">{r.city || '—'}</td>
                  <td className="px-5 py-3.5 text-white/75 whitespace-nowrap">{r.state || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PartnerAccountLayout>
  );
}
